import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import { getHolidayList, deleteHoliday } from '../services'
import { Holiday } from '../types/response'
import {
    getHolidayListColumns,
    getHolidayActions,
    getHolidayFilterItems,
    getHolidayFilterToggles,
} from '../utils/holiday-list-config'

interface HolidayFilters {
    year: number
    month: number
}

export const useHolidayList = () => {
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()
    const { t } = useTranslation()

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1

    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<HolidayFilters>({
        defaults: { sort_by: 'holiday_id', sort_order: 'desc' },
        filterDefaults: {
            year: currentYear,
            month: currentMonth,
        },
    })

    const { control } = useForm<HolidayFilters>({
        defaultValues: filters,
    })

    // Data fetching
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.HOLIDAYS, queryParams],
        queryFn: () => getHolidayList(queryParams),
        placeholderData: true,
    })

    // Delete mutation
    const { mutateAsync: doDeleteHoliday } = useMutationWithHandler(
        { mutationFn: deleteHoliday },
        {
            reactQueryKeys: QUERY_KEYS.HOLIDAYS,
            successMessage: t('delete-success'),
            errorMessage: t('something went wrong'),
            onSuccess: () => refetch(),
        }
    )

    // Delete handler
    const handleDelete = useCallback(
        (row: Holiday) => {
            setConfirmation({
                open: true,
                title: t('title-holiday-delete'),
                description: `${t('description-holiday-delete')} "${dayjs(row.holiday_date).format('DD/MM/YYYY')}"`,
                onConfirm: async () => {
                    try {
                        await doDeleteHoliday({ holiday_id: row.holiday_id })
                    } catch (error) {
                        console.error('Failed to delete holiday:', error)
                    }
                },
            })
        },
        [setConfirmation, t, doDeleteHoliday]
    )

    // Memoized configs
    const columns = useMemo(() => getHolidayListColumns(t), [t])

    const actions = useMemo(
        () => getHolidayActions({ t, navigate, onDelete: handleDelete }),
        [t, navigate, handleDelete]
    )

    const filterItems = useMemo(() => getHolidayFilterItems(t), [t])
    const filterToggles = useMemo(() => getHolidayFilterToggles(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        holidays: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Form & params
        control,
        setFilter,
        params,
        searchInputProps,

        // Configs
        columns,
        actions,
        filterItems,
        filterToggles,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    }
}
